import { type AppNode, type ImageNodeData, type TextNodeData, type VideoNodeData } from '../../types';

export type NodeUpdate = {
  id: string;
  position?: { x: number; y: number };
  width?: number;
  height?: number;
  zIndex?: number;
  data?: TextNodeData | ImageNodeData | VideoNodeData;
};

export type CreateOperation = {
  type: 'create';
  node: AppNode;
};

export type UpdateOperation = {
  type: 'update';
  update: NodeUpdate;
};

export type DeleteOperation = {
  type: 'delete';
  node: AppNode;
};

export type SyncOperation = CreateOperation | DeleteOperation | UpdateOperation;

export function getNodeId(op: SyncOperation): string {
  if (op.type === 'delete') {
    return op.node.id;
  }

  if (op.type === 'update') {
    return op.update.id;
  }

  return op.node.id;
}

/**
 * Merges two chronologically ordered sync operations for the same node.
 * Returns the merged operation, or null if they cancel out.
 */
export function mergeOperations(earlier: SyncOperation, later: SyncOperation): SyncOperation | null {
  // create + update — merge update fields into create node
  if (earlier.type === 'create' && later.type === 'update') {
    const node = { ...earlier.node };

    if (later.update.position) {
      node.position = later.update.position;
    }

    if (later.update.width != null || later.update.height != null) {
      node.style = {
        ...node.style,
        ...(later.update.width != null && { width: later.update.width }),
        ...(later.update.height != null && { height: later.update.height }),
      };
    }

    if (later.update.zIndex != null) {
      node.zIndex = later.update.zIndex;
    }

    return { node, type: 'create' };
  }

  // create + delete — cancel both
  if (earlier.type === 'create' && later.type === 'delete') {
    return null;
  }

  // update + update — merge updates
  if (earlier.type === 'update' && later.type === 'update') {
    return {
      type: 'update',
      update: {
        ...earlier.update,
        ...later.update,
      },
    };
  }

  // all other cases (including update + delete) — later wins
  return later;
}

/**
 * Squashes the queue by merging operations for the same node.
 * Iterates forward so existing is always earlier and op is always later.
 */
export function squashQueue(queue: SyncOperation[]): SyncOperation[] {
  const result = new Map<string, SyncOperation>();

  for (const op of queue) {
    const nodeId = getNodeId(op);
    const existing = result.get(nodeId);

    if (!existing) {
      result.set(nodeId, op);
      continue;
    }

    const merged = mergeOperations(existing, op);

    if (merged) {
      result.set(nodeId, merged);
    } else {
      result.delete(nodeId);
    }
  }

  return [...result.values()];
}
